import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { API_BASE } from "@/lib/utils";
import { getAuthHeaders } from "@/lib/auth-fetch";

const PROVIDERS = [
  { id: "revolut_platform", label: "Aivory Revolut (4% merchant fee)" },
  { id: "revolut", label: "Your Revolut Merchant account" },
  { id: "stripe", label: "Stripe" },
];

export default function RegistrationPaymentsPanel() {
  const [config, setConfig] = useState({
    provider: "revolut_platform",
    currency: "USD",
    stripePublishableKey: "",
    stripeSecretKey: "",
    revolutPublicKey: "",
    revolutSecretKey: "",
    revolutSandbox: false,
    usePlatformRevolut: true,
  });
  const [feePct, setFeePct] = useState(4);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/registration-payments`, { headers: getAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || res.statusText);
      if (data.config) setConfig((c) => ({ ...c, ...data.config }));
      if (data.platformMerchantFeePercent != null) setFeePct(data.platformMerchantFeePercent);
    } catch (err) {
      toast.error("Could not load registration payments", { description: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/registration-payments`, {
        method: "PATCH",
        headers: { "content-type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify(config),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || res.statusText);
      if (data.config) setConfig(data.config);
      toast.success("Registration payment settings saved");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-[var(--text-secondary)]">Loading payment settings…</p>;
  }

  return (
    <div className="surface p-6 space-y-5 max-w-2xl">
      <div>
        <h2 className="font-heading text-xl font-medium">Event registration payments</h2>
        <p className="text-sm text-[var(--text-secondary)] mt-2">
          Choose how paid ticket checkout is collected on your public register page. Aivory Revolut uses our merchant
          account with a {feePct}% platform fee on top of the ticket total.
        </p>
      </div>

      <label className="block">
        <span className="text-overline">Provider</span>
        <select
          className="input-dark mt-2 w-full"
          value={config.provider}
          onChange={(e) => setConfig((s) => ({ ...s, provider: e.target.value }))}
        >
          {PROVIDERS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-overline">Checkout currency</span>
        <input
          className="input-dark mt-2 w-full font-mono uppercase"
          value={config.currency}
          onChange={(e) => setConfig((s) => ({ ...s, currency: e.target.value.toUpperCase() }))}
        />
      </label>

      {config.provider === "stripe" ? (
        <div className="space-y-3 border-t border-[var(--border-default)] pt-4">
          <label className="block">
            <span className="text-overline">Stripe publishable key</span>
            <input
              className="input-dark mt-2 w-full font-mono text-sm"
              value={config.stripePublishableKey}
              onChange={(e) => setConfig((s) => ({ ...s, stripePublishableKey: e.target.value }))}
            />
          </label>
          <label className="block">
            <span className="text-overline">Stripe secret key</span>
            <input
              type="password"
              className="input-dark mt-2 w-full font-mono text-sm"
              placeholder="Leave blank to keep existing"
              value={config.stripeSecretKey}
              onChange={(e) => setConfig((s) => ({ ...s, stripeSecretKey: e.target.value }))}
            />
          </label>
        </div>
      ) : null}

      {config.provider === "revolut" ? (
        <div className="space-y-3 border-t border-[var(--border-default)] pt-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={Boolean(config.usePlatformRevolut)}
              onChange={(e) => setConfig((s) => ({ ...s, usePlatformRevolut: e.target.checked }))}
            />
            Use Aivory Revolut when my keys are not set
          </label>
          <label className="block">
            <span className="text-overline">Revolut secret key (optional)</span>
            <input
              type="password"
              className="input-dark mt-2 w-full font-mono text-sm"
              placeholder="Leave blank to use platform or keep existing"
              value={config.revolutSecretKey}
              onChange={(e) => setConfig((s) => ({ ...s, revolutSecretKey: e.target.value }))}
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={Boolean(config.revolutSandbox)}
              onChange={(e) => setConfig((s) => ({ ...s, revolutSandbox: e.target.checked }))}
            />
            Revolut sandbox
          </label>
        </div>
      ) : null}

      {config.provider === "revolut_platform" ? (
        <p className="text-xs text-[var(--text-tertiary)] font-mono border border-[var(--border-default)] px-3 py-2">
          Attendees pay via Aivory&apos;s Revolut checkout. A {feePct}% merchant fee is added to the ticket subtotal at
          checkout.
        </p>
      ) : null}

      <button type="button" className="btn-primary" onClick={save} disabled={saving}>
        {saving ? "Saving…" : "Save payment settings"}
      </button>
    </div>
  );
}
